/* eslint-disable @typescript-eslint/no-shadow */

import React, { useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  KIcon,
  KTextInput,
  KButton,
  KDialog,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import {
  deleteTransformation,
  getTransformations,
} from "../../apiCalls/config/transformations";
import { formatDate } from "../../utils/timeUtils";
import TableDropdown from "../../components/TableDropdown";
import { setAlert } from "../../redux/actions/actions";
import KInfiniteTable from "../../components/table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      updateMyData={props.updateMyData}
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data.length === newProps.data.length;
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
  tableDropDownWrapper: {
    cursor: "pointer",
    display: "flex",
    justifyContent: "flex-end",
    marginRight: 14,
  },
}));

const Transformations: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const firstUpdate = React.useRef(true);
  const { CancelToken } = axios;
  const dispatch = useDispatch();

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      cancelTokenSource,
      deleteModalOpen,
      transformationToDelete,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    cancelTokenSource: null,
    deleteModalOpen: false,
    transformationToDelete: "",
  });

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const loadTemplatesPage = (query, page, startEmpty = true) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({ isNextPageLoading: true, cancelTokenSource: source });
    getTransformations(query, page, source.token)
      .then((res) => {
        const previousArray = startEmpty ? [] : items;
        setState({
          items: [...previousArray].concat(
            ...res.data.map((x) => ({
              name: x.name,
              tags: Object.keys(x.tags) || "",
              createdAt: x.created_at,
              updatedAt: x.updated_at,
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: false,
          cancelToken: null,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () => {
    loadTemplatesPage(query, pagesLoaded + 1, false);
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadTemplatesPage(query, 1, true);
  }, [query]);

  const handleDelete = () => {
    deleteTransformation(transformationToDelete)
      .then(() => {
        dispatch(
          setAlert(`Successfully deleted ${transformationToDelete}`, "success"),
        );
        setState({
          deleteModalOpen: false,
          transformationToDelete: "",
        });
        loadTemplatesPage(query, 1, true);
      })
      .catch((e) => {
        setState({
          deleteModalOpen: false,
        });
        dispatch(
          setAlert(
            `Error deleting template ${transformationToDelete}`,
            "error",
          ),
        );
        console.error(e);
      });
  };

  const toggleDeletePopup = () =>
    setState({ deleteModalOpen: !deleteModalOpen, transformationToDelete: "" });

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleDeletePopup()}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the transformation{" "}
        <b>{transformationToDelete}</b>?
      </p>
    </KDialog>
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Tags",
        accessor: "tags",
      },
      {
        Header: "Created",
        accessor: (row) => formatDate(row.createdAt),
        defaultCanSort: true,
      },
      {
        Header: "Updated",
        accessor: (row) => formatDate(row.updatedAt),
        defaultCanSort: true,
      },

      {
        Header: "Actions",
        Cell: (row) => {
          return (
            <div className={classes.tableDropDownWrapper}>
              <TableDropdown
                menuItems={[
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () =>
                      history.push(
                        `/config/templates/transformations/${row.row.original.name}`,
                      ),
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    disabled: false,
                    action: () =>
                      setState({
                        deleteModalOpen: true,
                        transformationToDelete: row.row.original.name,
                      }),
                  },
                ]}
              />
            </div>
          );
        },
        width: 60,
      },
    ],
    [],
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <KTextInput
          placeholder="Search transformations..."
          value={query}
          onChange={handleChange}
          name="query"
        />
        <KButton
          children="New Transformation"
          icon={<KIcon icon="plus" />}
          onClick={() =>
            history.push("/config/templates/transformations/create")
          }
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          loadNextPage={loadNextPage}
          isNextPageLoading={isNextPageLoading}
          columns={columns}
          data={items}
          hasNextPage={hasNextPage}
        />
      </div>
      {deletePopup}
    </>
  );
};

export default Transformations;
